/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { lazy, Suspense, type ComponentType, type LazyExoticComponent } from 'react';

import { css, jsx } from '@compiled/react';
import type { IntlShape } from 'react-intl';

import type { ToolbarItem } from '@atlaskit/editor-common/extensions';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type {
	Command,
	DropdownOptions,
	Icon,
	PublicPluginAPI,
	RenderOptionsPropsT,
} from '@atlaskit/editor-common/types';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import ClockIcon from '@atlaskit/icon/core/clock';
import { token } from '@atlaskit/tokens';
import type { AiManagedObject } from '@atlassian/ai-managed-objects/types';

import type {
	AgentManagedExtensionAttributes,
	AgentManagedExtensionPlugin,
	AgentManagedExtensionPluginState,
} from '../../common/agentManagedExtensionPluginType';
import { messages } from '../../common/messages';
import {
	findAgentManagedExtension,
	findAgentManagedExtensionByLocalId,
	getNodeAiManagedObject,
} from '../../common/utils';
import {
	getAiManagedObjectWithCache,
	getCachedAiManagedObject,
	updateAiManagedObjectWithCache,
} from '../ai-managed-object-cache';
import { setNodeAiManagedObject } from '../editor-commands/agent-managed-object-node';
import {
	getAutoUpdateIntervalTargetFromState,
	getCachedAiManagedObjectByLocalIdFromPluginState,
	setMeta,
} from '../pm-plugins/state';

import {
	type ManagedBlockUpdateInterval,
	getUpdateIntervalFromDesiredFrequency,
	toAiManagedObjectConfigInputWithUpdateInterval,
} from './configuration';

type AutoUpdateIntervalOption = ManagedBlockUpdateInterval | 'none';

type AutoUpdateIntervalIconComponent = ComponentType<{ label: string }>;
type AutoUpdateIntervalLazyIconComponent = LazyExoticComponent<AutoUpdateIntervalIconComponent>;

type AutoUpdateIntervalDropdownOption = {
	Icon: AutoUpdateIntervalLazyIconComponent;
	id: string;
	label: string;
	option: AutoUpdateIntervalOption;
};

const StatusWorkflowStoppedIcon = lazy(
	() =>
		import(
			/* webpackChunkName: "@atlaskit-internal_icon-lab-status-workflow-stopped" */ '@atlaskit/icon-lab/core/status-workflow-stopped'
		),
);

const SpeedometerRightDownIcon = lazy(
	() =>
		import(
			/* webpackChunkName: "@atlaskit-internal_icon-lab-speedometer-right-down" */ '@atlaskit/icon-lab/core/speedometer-right-down'
		),
);

const SpeedometerUpIcon = lazy(
	() =>
		import(
			/* webpackChunkName: "@atlaskit-internal_icon-lab-speedometer-up" */ '@atlaskit/icon-lab/core/speedometer-up'
		),
);

const SpeedometerLeftDownIcon = lazy(
	() =>
		import(
			/* webpackChunkName: "@atlaskit-internal_icon-lab-speedometer-left-down" */ '@atlaskit/icon-lab/core/speedometer-left-down'
		),
);

const menuStyles = css({
	boxSizing: 'border-box',
	paddingBlock: token('space.075'),
	paddingInline: token('space.075'),
	width: '100%',
});

const optionStyles = css({
	alignItems: 'center',
	backgroundColor: 'transparent',
	border: 0,
	borderRadius: token('radius.small'),
	boxSizing: 'border-box',
	color: token('color.text'),
	cursor: 'pointer',
	display: 'flex',
	gap: token('space.100'),
	minHeight: '36px',
	paddingBlock: token('space.075'),
	paddingInline: token('space.100'),
	textAlign: 'left',
	width: '100%',

	'&:hover': {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
	},

	'&:focus-visible': {
		outlineColor: token('color.border.focused'),
		outlineOffset: token('space.025'),
		outlineStyle: 'solid',
		outlineWidth: token('border.width.focused'),
	},
});

const selectedOptionStyles = css({
	backgroundColor: token('color.background.selected'),
	color: token('color.text.selected'),

	'&:hover': {
		backgroundColor: token('color.background.selected.hovered'),
	},
});

const optionIconStyles = css({
	display: 'flex',
	flexShrink: 0,
});

const optionIconFallbackStyles = css({
	display: 'inline-block',
	height: '24px',
	width: '24px',
});

const optionLabelStyles = css({
	font: token('font.body'),
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
});

const getAutoUpdateIntervalOptions = (intl: IntlShape): AutoUpdateIntervalDropdownOption[] => [
	{
		Icon: StatusWorkflowStoppedIcon,
		id: 'agent-managed-extension-auto-update-interval-none',
		label: intl.formatMessage(messages.noAutoUpdate),
		option: 'none',
	},
	{
		Icon: SpeedometerRightDownIcon,
		id: 'agent-managed-extension-auto-update-interval-short',
		label: intl.formatMessage(messages.updateIntervalShort),
		option: 'short',
	},
	{
		Icon: SpeedometerUpIcon,
		id: 'agent-managed-extension-auto-update-interval-medium',
		label: intl.formatMessage(messages.updateIntervalMedium),
		option: 'medium',
	},
	{
		Icon: SpeedometerLeftDownIcon,
		id: 'agent-managed-extension-auto-update-interval-long',
		label: intl.formatMessage(messages.updateIntervalLong),
		option: 'long',
	},
];

type SelectedAiManagedObject = {
	aimoId?: string;
	cachedAimo?: AiManagedObject;
	localId?: string;
};

const getSelectedAiManagedObject = (editorView: EditorView): SelectedAiManagedObject => {
	const found = findAgentManagedExtension(editorView.state);
	const attrs = found?.node.attrs as AgentManagedExtensionAttributes | undefined;
	const aimoId = getNodeAiManagedObject(attrs)?.id;

	return {
		aimoId,
		cachedAimo: aimoId ? getCachedAiManagedObject(editorView, aimoId) : undefined,
		localId: found?.node.attrs.localId as string | undefined,
	};
};

type SharedStateSelectorArgs = {
	agentManagedExtensionState?: AgentManagedExtensionPluginState;
};

const sharedStateSelector = ({ agentManagedExtensionState }: SharedStateSelectorArgs) =>
	agentManagedExtensionState;

const useSelectedUpdateInterval = (
	api: PublicPluginAPI<[AgentManagedExtensionPlugin]> | undefined,
): AutoUpdateIntervalOption | undefined => {
	const pluginState = useSharedPluginStateWithSelector(
		api,
		['agentManagedExtension'],
		sharedStateSelector,
	);
	const localId = pluginState?.isAutoUpdateIntervalOpen
		? pluginState.autoUpdateIntervalLocalId
		: undefined;
	const aimo = getCachedAiManagedObjectByLocalIdFromPluginState(pluginState, localId);

	return aimo
		? (getUpdateIntervalFromDesiredFrequency(aimo.config.desiredUpdateFrequency) ?? 'none')
		: undefined;
};

const dispatchToggleAutoUpdateInterval = (
	editorView: EditorView,
	selected: SelectedAiManagedObject,
): boolean => {
	const currentTarget = getAutoUpdateIntervalTargetFromState(editorView.state);
	const isClosingCurrentTarget = currentTarget.isOpen && currentTarget.localId === selected.localId;

	editorView.dispatch(
		setMeta(editorView.state.tr, {
			localId: selected.localId,
			type: 'TOGGLE_AUTO_UPDATE_INTERVAL' as const,
		}),
	);

	return !isClosingCurrentTarget;
};

const getAiManagedObjectSelectionByLocalId = (
	editorView: EditorView,
	localId: string,
): SelectedAiManagedObject => {
	const selected = getSelectedAiManagedObject(editorView);
	if (selected.localId === localId) {
		return selected;
	}

	const found = findAgentManagedExtensionByLocalId(editorView.state, localId);
	const attrs = found?.node.attrs as AgentManagedExtensionAttributes | undefined;
	const aimoId = getNodeAiManagedObject(attrs)?.id;
	return {
		aimoId,
		cachedAimo: aimoId ? getCachedAiManagedObject(editorView, aimoId) : undefined,
		localId,
	};
};

const hydrateSelectionFromEditorView = async (
	editorView: EditorView,
	selected: SelectedAiManagedObject,
): Promise<void> => {
	if (!selected.aimoId) {
		return;
	}

	await getAiManagedObjectWithCache({
		aimoId: selected.aimoId,
		cachedAimo: selected.cachedAimo,
		editorView,
		localId: selected.localId,
	});
};

const persistUpdateInterval = async (
	editorView: EditorView,
	updateInterval?: ManagedBlockUpdateInterval,
): Promise<void> => {
	const target = getAutoUpdateIntervalTargetFromState(editorView.state);
	const selected = target.localId
		? getAiManagedObjectSelectionByLocalId(editorView, target.localId)
		: getSelectedAiManagedObject(editorView);
	if (!selected.aimoId) {
		return;
	}

	const aimo = await getAiManagedObjectWithCache({
		aimoId: selected.aimoId,
		cachedAimo: selected.cachedAimo,
		editorView,
		localId: selected.localId,
	});
	if (!aimo) {
		return;
	}

	const updatedAimo = await updateAiManagedObjectWithCache(
		editorView,
		{
			id: aimo.id,
			config: toAiManagedObjectConfigInputWithUpdateInterval(aimo.config, updateInterval),
		},
		selected.localId,
	);

	const latestTarget = getAutoUpdateIntervalTargetFromState(editorView.state);
	if (!latestTarget.isOpen || latestTarget.localId !== selected.localId) {
		return;
	}

	const tr = editorView.state.tr;
	setNodeAiManagedObject(editorView.state, tr, updatedAimo, selected.localId);
	setMeta(tr, { type: 'CLOSE_AUTO_UPDATE_INTERVAL' });
	editorView.dispatch(tr);
};

const createUpdateIntervalCommand =
	(option: AutoUpdateIntervalOption): Command =>
	(_state, _dispatch, editorView?: EditorView) => {
		if (!editorView) {
			return false;
		}

		void persistUpdateInterval(editorView, option === 'none' ? undefined : option);
		return true;
	};

const createHydrateSelectionCommand =
	(): Command => (_state, _dispatch, editorView?: EditorView) => {
		if (!editorView) {
			return false;
		}

		const selected = getSelectedAiManagedObject(editorView);
		const isOpening = dispatchToggleAutoUpdateInterval(editorView, selected);
		if (isOpening) {
			void hydrateSelectionFromEditorView(editorView, selected);
		}
		return true;
	};

type AutoUpdateIntervalToolbarMenuProps = {
	api?: PublicPluginAPI<[AgentManagedExtensionPlugin]>;
	dispatchCommand: RenderOptionsPropsT<Command>['dispatchCommand'];
	hide: RenderOptionsPropsT<Command>['hide'];
	intl: IntlShape;
	label: string;
};

const AutoUpdateIntervalToolbarMenu = ({
	api,
	dispatchCommand,
	hide,
	intl,
	label,
}: AutoUpdateIntervalToolbarMenuProps): JSX.Element => {
	const selectedUpdateInterval = useSelectedUpdateInterval(api);

	const options = getAutoUpdateIntervalOptions(intl);

	return (
		<div aria-label={label} css={menuStyles} role="menu">
			{options.map((option) => {
				const Icon = option.Icon;
				const isSelected = (selectedUpdateInterval ?? 'none') === option.option;
				return (
					<button
						aria-checked={isSelected}
						css={[optionStyles, isSelected && selectedOptionStyles]}
						data-testid={option.id}
						key={option.id}
						onClick={() => {
							dispatchCommand(createUpdateIntervalCommand(option.option));
							hide();
						}}
						role="menuitemcheckbox"
						type="button"
					>
						<span css={optionIconStyles}>
							<Suspense fallback={<span css={optionIconFallbackStyles} />}>
								<Icon label="" />
							</Suspense>
						</span>
						<span css={optionLabelStyles}>{option.label}</span>
					</button>
				);
			})}
		</div>
	);
};

export const getAutoUpdateIntervalToolbarItem = (
	intl: IntlShape,
	api?: PublicPluginAPI<[AgentManagedExtensionPlugin]>,
): ToolbarItem => {
	const label = intl.formatMessage(messages.autoUpdateInterval);
	const dropdownOptions: DropdownOptions<Command> = {
		height: 168,
		render: ({ dispatchCommand, hide }) => (
			<AutoUpdateIntervalToolbarMenu
				api={api}
				dispatchCommand={dispatchCommand}
				hide={hide}
				intl={intl}
				label={label}
			/>
		),
		width: 220,
	};

	return {
		type: 'dropdown',
		id: 'agent-managed-extension-auto-update-interval',
		title: label,
		tooltip: label,
		icon: (<ClockIcon label={label} />) as unknown as Icon,
		dropdownWidth: 220,
		hideExpandIcon: true,
		onToggle: createHydrateSelectionCommand(),
		showSelected: false,
		testId: 'agent-managed-extension-auto-update-interval-trigger',
		options: dropdownOptions,
	};
};
